import { may, type CanHolder } from "../../lib/access";

/**
 * Which incident affordances to show (ADR-API-09: the server re-checks every
 * write). Each one names the operation of the rights matrix ([F-027]) it
 * performs and asks the server's answer: `/me.can` for creating, the
 * incident's own `can` for everything done on an incident. Nothing here knows
 * which role may what - that is the matrix's, and the server's.
 */

/** May open the create form (`incident.create`, the Melder role). */
export function canCreate(user: CanHolder | null | undefined): boolean {
  return may(user, "incident.create");
}

/** May change title, description, category, severity and areas (`incident.edit`). */
export function canEdit(incident: CanHolder): boolean {
  return may(incident, "incident.edit");
}

/** May change the status (`incident.status`, Owner or assigned Bearbeiter). */
export function canChangeStatus(incident: CanHolder): boolean {
  return may(incident, "incident.status");
}

/** May assign and remove tags (`incident.tags`). */
export function canEditTags(incident: CanHolder): boolean {
  return may(incident, "incident.tags");
}

/** May write a comment, or another manual timeline entry. */
export function canComment(incident: CanHolder): boolean {
  return may(incident, "timeline.comment") || may(incident, "timeline.entry");
}

/** May write a manual timeline entry other than a comment (`timeline.entry`). */
export function canWriteTimelineEntry(incident: CanHolder): boolean {
  return may(incident, "timeline.entry");
}

/** May take this incident - it has no Owner, and the caller may take (`incident.take`). */
export function canTake(incident: CanHolder & { ownerId: string | null }): boolean {
  return incident.ownerId === null && may(incident, "incident.take");
}

/** May set Owner and Bearbeiter - the Owner of this incident (`incident.assign`). */
export function canAssign(incident: CanHolder): boolean {
  return may(incident, "incident.assign");
}

/**
 * May name a new Owner because the current one is deactivated
 * (`incident.reassign-inactive-owner`). Whether the Owner is deactivated is
 * the caller's to say - the users list knows.
 */
export function canReassignRetiredOwner(incident: CanHolder, ownerRetired: boolean): boolean {
  return ownerRetired && may(incident, "incident.reassign-inactive-owner");
}

/** May set or clear the GR-004 legal hold (`incident.legal-hold`). */
export function canManageLegalHold(incident: CanHolder): boolean {
  return may(incident, "incident.legal-hold");
}

/** May reopen a closed incident (`incident.reopen`: its Owner, or Admin). */
export function canReopen(incident: CanHolder): boolean {
  return may(incident, "incident.reopen");
}
